import type { ReactNode } from 'react';
import React from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router';

import Link from '@swf/components/Link/Link';
import Translation from '@swf/components/Translation/Translation';
import { Country, EulaType, ResolverTag, Site } from '@swf/core/enums';
import useResolver from '@swf/core/hooks/useResolver';
import { select } from '@swf/core/store';
import { externalLink } from '@swf/core/utils';

import {
  TXTCLI_By_checking_this_box_I_confirm_ELL,
  TXTCLI_I_confirm_that_I_am_at_least_N_ELL,
  TXTCLI_I_confirm_that_I_am_at_least_N_ELL_1,
  TXTCLI_I_consent_to_receive_details_a_ELL,
  TXTCLI_I_have_read_and_understood_the_ELL_6,
  TXTCLI_I_have_read_and_understood_the_ELL_7,
  TXTCLI_I_have_read_and_understood_the_ELL_8,
  TXTCLI_I_understand_that_I_can_always_ELL,
  TXTCLI_We_do_not_offer_real_money_pla_ELL_2,
  TXTMOB_I_consent_to_receive_details_o_ELL,
  TXTWCL_Underage_gambling_is_an_offenc_ELL
} from './Label.messages.json';

interface Props {
  eulaType: EulaType;
  variant: 'first' | 'stars';
  onEulaLinkClick?: () => void;
  onPrivacyLinkClick?: () => void;
}

enum AREulaState {
  BUENOS_AIRES = 'B',
  CABA = 'C'
}

const isAREulaState = (country?: string, state?: string) =>
  country === Country.Argentina && Object.values(AREulaState).includes(state as AREulaState);

const Label: React.FC<Props> = ({ variant, eulaType, onEulaLinkClick, onPrivacyLinkClick }) => {
  const history = useHistory<{ country: string; state: string }>();
  const legalAge = useSelector(select.site.getLegalAge);
  const siteId = useSelector(select.site.getId);
  const mcEula = useResolver(ResolverTag.MCEula);
  const mcPrivacy = useResolver(ResolverTag.MCPrivacy);
  const mcEulaArStates = useResolver(ResolverTag.MCEulaARStates);
  const gamingAccountAgreement = useResolver(ResolverTag.GamingAccountAgreement);

  let eulaResolverTag = '';
  if (variant === 'stars') {
    const { country, state } = history?.location?.state || {};
    eulaResolverTag = isAREulaState(country, state) ? mcEulaArStates : mcEula;
  }

  const trackableLink = (msg: ReactNode, onClick?: () => void) => (
    <Link
      onClick={(e: React.MouseEvent) => {
        e.preventDefault();
        onClick?.();
      }}
    >
      {msg}
    </Link>
  );

  const labels = {
    first: {
      [EulaType.Eula]: (
        <Translation
          {...TXTCLI_I_confirm_that_I_am_at_least_N_ELL}
          values={{
            0: legalAge,
            a: (msg: ReactNode) => trackableLink(msg, onEulaLinkClick)
          }}
        />
      ),
      [EulaType.Privacy]: (
        <Translation
          {...TXTCLI_I_have_read_and_understood_the_ELL_6}
          values={{
            a: (msg: ReactNode) => trackableLink(msg, onPrivacyLinkClick)
          }}
        />
      ),
      [EulaType.CombinedEulaPrivacy]: (
        <Translation
          {...TXTCLI_I_confirm_that_I_am_at_least_N_ELL_1}
          values={{
            0: legalAge,
            a: (msg: ReactNode) => trackableLink(msg, onEulaLinkClick),
            a2: (msg: ReactNode) => trackableLink(msg, onPrivacyLinkClick)
          }}
        />
      )
    },
    stars: {
      [EulaType.Eula]: (
        <p>
          {siteId === Site.UK && (
            <>
              <Translation tagName="b" {...TXTWCL_Underage_gambling_is_an_offenc_ELL} />{' '}
            </>
          )}
          <Translation
            {...TXTCLI_I_confirm_that_I_am_at_least_N_ELL}
            values={{ 0: legalAge, a: externalLink(eulaResolverTag) }}
          />
        </p>
      ),
      [EulaType.Privacy]: (
        <Translation
          {...TXTCLI_I_have_read_and_understood_the_ELL_6}
          values={{ a: externalLink(mcPrivacy) }}
        />
      ),
      [EulaType.CombinedEulaPrivacy]: (
        <Translation
          tagName="p"
          {...TXTCLI_I_confirm_that_I_am_at_least_N_ELL_1}
          values={{
            0: legalAge,
            a: externalLink(eulaResolverTag),
            a2: externalLink(mcPrivacy)
          }}
        />
      ),
      [EulaType.MarketingMailList]: (
        <p>
          {siteId === Site.IT ? (
            <>
              <Translation {...TXTCLI_I_consent_to_receive_details_a_ELL} />{' '}
              <Translation {...TXTCLI_I_understand_that_I_can_always_ELL} />
            </>
          ) : (
            <Translation {...TXTMOB_I_consent_to_receive_details_o_ELL} />
          )}
        </p>
      )
    },
    default: {
      [EulaType.GamingAccAgreement]: (
        <Translation
          tagName="p"
          {...TXTCLI_I_have_read_and_understood_the_ELL_7}
          values={{ a: externalLink(gamingAccountAgreement) }}
        />
      ),
      [EulaType.GameRulesConditions]: (
        <Translation tagName="p" {...TXTCLI_I_have_read_and_understood_the_ELL_8} />
      ),
      [EulaType.IndianStatesRestrictions]: (
        <p>
          <Translation {...TXTCLI_We_do_not_offer_real_money_pla_ELL_2} />{' '}
          <Translation {...TXTCLI_By_checking_this_box_I_confirm_ELL} />
        </p>
      )
    }
  };

  return labels[variant][eulaType] || labels.default[eulaType];
};

export default Label;
